<template>
  <div class="overview-milestone-leiste mb-6" data-testid="overview-milestone-timeline">
    <div class="flex items-center justify-between mb-4">
      <h2 class="overview-section-title m-0">
        {{ $t('UserProjects.overview.milestones.title') }}
      </h2>
      <router-link
        to="/user-projects/milestones"
        class="text-sm font-medium text-[var(--color-orange)] no-underline hover:underline"
      >
        {{ $t('UserProjects.overview.milestones.viewAll') }}
      </router-link>
    </div>

    <div
      v-if="!sortedMilestones.length"
      class="overview-milestone-leiste__empty p-8 text-center text-sm text-[var(--color-disabled)]"
      data-testid="overview-milestone-empty"
    >
      {{ $t('UserProjects.overview.milestones.empty') }}
    </div>

    <div v-else class="overview-milestone-leiste__panel p-5">
      <div class="flex items-center gap-1 overflow-x-auto pb-1">
        <template v-for="(item, index) in sortedMilestones" :key="item.id">
          <button
            type="button"
            class="overview-milestone-chip"
            :class="chipClasses(item, index)"
            :data-testid="`overview-milestone-card-${item.id}`"
            @click="$emit('select', item)"
          >
            <i :class="chipIconClass(item)" class="overview-milestone-chip__icon" aria-hidden="true" />
            <div class="text-left min-w-0">
              <p class="overview-milestone-chip__title">{{ item.title }}</p>
              <p class="overview-milestone-chip__date">{{ formatChipDate(item) }}</p>
            </div>
            <span class="overview-milestone-chip__badge" :class="badgeClass(item)">
              {{ badgeLabel(item) }}
            </span>
          </button>
          <div
            v-if="index < sortedMilestones.length - 1"
            class="overview-milestone-connector shrink-0"
            aria-hidden="true"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

const STATUS_CONFIG = {
  completed: {
    labelKey: 'UserProjects.overview.milestones.chip.completed',
    badge: 'overview-milestone-chip__badge--completed',
    icon: 'pi pi-check',
    iconColor: 'overview-milestone-chip__icon--completed',
  },
  on_track: {
    labelKey: 'UserProjects.overview.milestones.chip.onTrack',
    badge: 'overview-milestone-chip__badge--on-track',
    icon: 'pi pi-circle',
    iconColor: 'overview-milestone-chip__icon--on-track',
  },
  at_risk: {
    labelKey: 'UserProjects.overview.milestones.chip.atRisk',
    badge: 'overview-milestone-chip__badge--at-risk',
    icon: 'pi pi-clock',
    iconColor: 'overview-milestone-chip__icon--at-risk',
  },
  overdue: {
    labelKey: 'UserProjects.overview.milestones.chip.overdue',
    badge: 'overview-milestone-chip__badge--overdue',
    icon: 'pi pi-clock',
    iconColor: 'overview-milestone-chip__icon--overdue',
  },
};

export default {
  name: 'OverviewMilestoneTimeline',
  emits: ['select'],
  computed: {
    ...mapGetters('pmCommitment', {
      overview: 'overview',
    }),
    milestones() {
      return this.overview?.milestones ?? [];
    },
    sortedMilestones() {
      return [...this.milestones].sort((a, b) => {
        const aDate = new Date(a.dueDate || 0).getTime();
        const bDate = new Date(b.dueDate || 0).getTime();
        return aDate - bDate;
      });
    },
    currentIndex() {
      return this.sortedMilestones.findIndex((item) => item.status !== 'done');
    },
  },
  methods: {
    mapStatus(item) {
      if (item.status === 'done') return 'completed';
      if (item.isOverdue) return 'overdue';
      if (item.status === 'at_risk' || item.forecastHint === 'at_risk') return 'at_risk';
      return 'on_track';
    },
    statusConfig(item) {
      return STATUS_CONFIG[this.mapStatus(item)] || STATUS_CONFIG.on_track;
    },
    isCurrent(_item, index) {
      return this.currentIndex >= 0 && index === this.currentIndex;
    },
    chipClasses(item, index) {
      const status = this.mapStatus(item);
      return {
        'overview-milestone-chip--current': this.isCurrent(item, index),
        [`overview-milestone-chip--${status}`]: true,
      };
    },
    chipIconClass(item) {
      const config = this.statusConfig(item);
      return [config.icon, config.iconColor];
    },
    badgeClass(item) {
      return this.statusConfig(item).badge;
    },
    badgeLabel(item) {
      return this.$t(this.statusConfig(item).labelKey);
    },
    formatChipDate(item) {
      const value = item.status === 'done' ? item.actualDate || item.dueDate : item.dueDate;
      if (!value) return '—';
      return new Date(value).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' });
    },
  },
};
</script>

<style scoped>
.overview-section-title {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--color-dark);
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
}

.overview-milestone-leiste__empty,
.overview-milestone-leiste__panel {
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 14px;
}

.overview-milestone-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  flex-shrink: 0;
  max-width: min(100%, 16rem);
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.overview-milestone-chip:hover {
  background: #f5f5f5;
}

.overview-milestone-chip--current {
  border-color: var(--color-orange, #e4821a);
  background: #fdf0e6;
}

.overview-milestone-chip__icon {
  font-size: 0.875rem;
  flex-shrink: 0;
}

.overview-milestone-chip__icon--completed {
  color: var(--color-success-text, #22c55e);
}

.overview-milestone-chip__icon--on-track {
  color: var(--color-disabled, #ababab);
  font-size: 0.5rem;
}

.overview-milestone-chip__icon--at-risk {
  color: var(--color-orange, #e4821a);
}

.overview-milestone-chip__icon--overdue {
  color: var(--color-error-text, #ef4444);
}

.overview-milestone-chip__title {
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  color: var(--color-dark, #1e2019);
  line-height: 1.25;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.overview-milestone-chip__date {
  margin: 0;
  font-size: 11px;
  color: var(--color-disabled, #ababab);
}

.overview-milestone-chip__badge {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 0.125rem 0.375rem;
  border-radius: 999px;
  white-space: normal;
  text-align: center;
  line-height: 1.2;
  flex-shrink: 0;
}

.overview-milestone-chip__badge--completed {
  background: #dcfce7;
  color: #166534;
}

.overview-milestone-chip__badge--on-track {
  background: #f2f2f2;
  color: #6b6b6b;
}

.overview-milestone-chip__badge--at-risk {
  background: #fef9c3;
  color: #854d0e;
}

.overview-milestone-chip__badge--overdue {
  background: #fee2e2;
  color: #991b1b;
}

.overview-milestone-connector {
  width: 1rem;
  height: 1px;
  background: #e5e5e5;
}
</style>
